// @ts-nocheck
import { AccordionSectionPanel } from "@insite/mobius/AccordionSection";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import Styleguide from "@toolkit/Framework/Types/Styleguide";
import { css } from "styled-components";
import { TransitionStatus } from "react-transition-group/Transition";

/* Store Custom Code - Start */
import MinusCircleLine from "@store/Library/Components/Toolkit/Icons/MinusCircle/MinusCircleLine";
import PlusCircleLine from "@store/Library/Components/Toolkit/Icons/PlusCircle/PlusCircleLine";
/* Store Custom Code - End */

const _sectionHeaderButtonBaseStyles = () => {
    return css`
        border: none;
        /* Bunzl Custom Code */
        color: ${getColor("text.main")};
        line-height: ${({ theme }) => theme.design.typography.lineHeight.base};
        transition: ${transitionHoverStyles("color")};

        ${breakpoint("xs")} {
            padding: ${pxToRem(12)} ${pxToRem(20)};
            margin: ${pxToRem(8)} 0;
            background-color: ${getColor("common.accentLighter")};
        }

        ${breakpoint("md")} {
            position: relative;
            padding: ${pxToRem(2)};
            background-color: ${getColor("common.background")};
        }

        &:hover * {
            /* Bunzl Custom Code */
            color: ${getColor("primary")};
        }

        &[aria-expanded="true"] {
            border-color: transparent;
        }
    `;
};

const _sectionHeaderButtonFocusStyles = () => {
    return css`
        /* Bunzl Custom Code */
        &:focus,
        &:focus-visible {
            ${_sectionHeaderButtonBaseStyles()}
            outline-color: ${getProp("theme.focus.color")};
            outline-style: ${getProp("theme.focus.style")};
            outline-width: ${getProp("theme.focus.width")};
            outline-offset: -${getProp("theme.focus.width")};
        }
        /* Bunzl Custom Code */
        :focus:not(:focus-visible) {
            outline: none;
        }
    `;
};

const accordion: Styleguide["accordion"] = {
    sectionDefaultProps: {
        titleTypographyProps: {
            color: "primary",
            forwardAs: "h3",
            variant: "h3",
            transform: "initial",
            css: css`
                margin-bottom: 0;
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: ${({ theme }) => theme.design.typography.size.base};
                                font-weight: ${({ theme }) => theme.design.typography.weight.medium};
                            `,
                        ],
                        mediaOptions.max,
                    )}
            `,
        },
        headerProps: {
            css: css`
                button {
                    ${_sectionHeaderButtonBaseStyles()}
                    ${_sectionHeaderButtonFocusStyles()}
                }

                ${/* sc-selector */ AccordionSectionPanel} + & {
                    margin-top: ${pxToRem(1)};

                    button {
                        ${_sectionHeaderButtonBaseStyles()}
                        ${_sectionHeaderButtonFocusStyles()}
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                border: none;
                /* Store Custom Code - animated accordion panel */
                display: grid !important;
                grid-template-rows: ${({ hidden }: { hidden?: boolean }) => (hidden ? "0fr" : "1fr")};
                grid-auto-rows: ${({ hidden }: { hidden?: boolean }) => (hidden ? "0fr" : "min-content")};
                transition:
                    grid-template-rows 0.25s ease-in-out,
                    grid-auto-rows 0.25s ease-in-out,
                    padding-top 0.25s ease-in-out,
                    padding-bottom 0.25s ease-in-out,
                    margin-bottom 0.25s ease-in-out,
                    border-width 0.25s ease-in-out;
                > * {
                    overflow: ${({ hidden }: { hidden?: boolean }) => (hidden ? "hidden" : "visible")};
                    transition: overflow 0s ${({ hidden }: { hidden?: boolean }) => (hidden ? "0s" : "0.25s")}
                        allow-discrete;
                }

                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                /* Bunzl Custom Code */
                                padding: ${pxToRem(16)} ${pxToRem(32)} ${pxToRem(16)};
                            `,
                            null,
                            css`
                                padding: 2.25em ${pxToRem(2)};
                                border-bottom: ${pxToRem(1)} solid ${getColor("common.border")};
                            `,
                        ],
                        mediaOptions.min,
                    )}

                &[hidden] {
                    padding-top: 0 !important;
                    padding-bottom: 0 !important;
                    margin: 0;
                    border: none;
                }
            `,
        },
        toggleIconProps: {
            color: "inherit",
            src: PlusCircleLine, // Store Custom Code
            css: css`
                height: ${({ theme }) => theme.design.typography.lineHeight.base};
                width: ${({ theme }) => theme.design.typography.lineHeight.base};
                position: relative;
                /* Bunzl Custom Code */
                :hover {
                    color: ${getColor("primary")};
                }
                svg {
                    width: inherit;
                    height: inherit;
                    transform: scale(1.231);
                }
            `,
        },
        /* Store Custom Code - Start */
        collapseIconProps: {
            src: MinusCircleLine,
        },
        toggleTransition: {
            timeout: { enter: 150, exit: 300 },
            transitionCss: css`
                transition: opacity 150ms ease-in;
                ${({ transitionState }: { transitionState: TransitionStatus }) =>
                    transitionState === "entering" ? "opacity: 1;" : ""}
            `,
        },
        /* Store Custom Code - End */
    },
};

export default accordion;
